import React, { useCallback, useContext, useEffect, useState } from 'react';
import { EditOutlined } from '@ant-design/icons';
import {
  Button,
  Checkbox,
  DatePicker,
  Divider,
  Form,
  Input,
  Modal,
  Select,
  Space,
} from 'antd';
import dayjs, { Dayjs } from 'dayjs';

import {
  BaselineConditions,
  emptyBaselineConditions,
  PROGRAM_STATUSES,
  PROGRAM_TYPES,
} from '@totem/components/portfolio/buildings/details/optimize/types';
import ProgramDashboardContext from '@totem/components/services/optimize/v2/dashboard/ProgramDashboardContext';
import ModalFooter from '@totem/components/ui_components/dialogs/ModalFooter';
import ModalTitle from '@totem/components/ui_components/dialogs/ModalTitle';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { BUILDINGS_ENDPOINT } from '@totem/utilities/endpoints';
import { CheckResponseShowError } from '@totem/utilities/responseUtilities';

const styles = {
  row: { display: 'flex', gap: '4%' },
  half: { paddingBottom: '0', marginBottom: '1rem', width: '48%' },
  full: { paddingBottom: '0', marginBottom: '1rem', width: '100%' },
};

const ProgramEditButton = () => {
  const { getApiToken } = useContext(UserProfileContext);
  const { buildingId, programId, dashboard, requestRefresh } = useContext(
    ProgramDashboardContext,
  );

  const [open, setOpen] = useState(false);
  const [isSending, setIsSending] = useState(false);

  const [name, setName] = useState('');
  const [status, setStatus] = useState('active');
  const [types, setTypes] = useState<string[]>([]);
  const [baselineStart, setBaselineStart] = useState<Dayjs | null>(null);
  const [baselineEnd, setBaselineEnd] = useState<Dayjs | null>(null);
  const [occupancyStatus, setOccupancyStatus] = useState('');
  const [notes, setNotes] = useState('');
  const [conditions, setConditions] = useState<BaselineConditions>(
    emptyBaselineConditions(),
  );

  // Populate form from current program data when opening
  useEffect(() => {
    if (open && dashboard?.program) {
      const prog = dashboard.program;
      setName(prog.name ?? '');

      setStatus(prog.status ?? 'active');

      setTypes(prog.types ?? []);

      setBaselineStart(
        prog.baselinePeriodStart ? dayjs(prog.baselinePeriodStart) : null,
      );

      setBaselineEnd(
        prog.baselinePeriodEnd ? dayjs(prog.baselinePeriodEnd) : null,
      );

      setOccupancyStatus(prog.occupancyStatus ?? '');

      setNotes(prog.notes ?? '');

      setConditions(prog.baselineConditions ?? emptyBaselineConditions());
    }
  }, [open, dashboard]);

  const updateCondition = <Key extends keyof BaselineConditions>(
    key: Key,
    value: BaselineConditions[Key],
  ) => setConditions((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = useCallback(() => {
    if (isSending || !name.trim()) {
      return;
    }
    setIsSending(true);

    const payload = {
      name,
      status,
      types,
      baselinePeriodStart: baselineStart
        ? baselineStart.startOf('month').toISOString()
        : null,
      baselinePeriodEnd: baselineEnd
        ? baselineEnd.endOf('month').toISOString()
        : null,
      occupancyStatus,
      notes,
      baselineConditions: conditions,
    };

    fetch(
      `${BUILDINGS_ENDPOINT}/${buildingId}/optimize/programs/${programId}`,
      {
        method: 'PATCH',
        headers: new Headers({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getApiToken()}`,
        }),
        body: JSON.stringify(payload),
      },
    )
      .then((res) => {
        CheckResponseShowError(res);

        requestRefresh();

        setOpen(false);
      })
      .finally(() => setIsSending(false));
  }, [
    isSending,
    name,
    status,
    types,
    baselineStart,
    baselineEnd,
    occupancyStatus,
    notes,
    conditions,
    buildingId,
    programId,
    getApiToken,
    requestRefresh,
  ]);

  return (
    <>
      <Button icon={<EditOutlined />} onClick={() => setOpen(true)}>
        Edit Program
      </Button>

      <Modal
        open={open}
        onCancel={() => setOpen(false)}
        width={640}
        title={<ModalTitle>Edit Program</ModalTitle>}
        footer={
          <ModalFooter>
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button
              type="primary"
              loading={isSending}
              disabled={!name.trim()}
              onClick={handleSubmit}
            >
              Save
            </Button>
          </ModalFooter>
        }
      >
        <Form layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item label="Program Name" required style={styles.full}>
            <Input
              value={name}
              onChange={(evt) => setName(evt.target.value)}
              placeholder="e.g. Energy Reduction 2025"
            />
          </Form.Item>

          <div style={styles.row}>
            <Form.Item label="Status" style={styles.half}>
              <Select
                value={status}
                onChange={setStatus}
                options={PROGRAM_STATUSES}
                style={{ width: '100%' }}
              />
            </Form.Item>
            <Form.Item label="Occupancy Status" style={styles.half}>
              <Input
                value={occupancyStatus}
                onChange={(evt) => setOccupancyStatus(evt.target.value)}
                placeholder="e.g. Occupied"
              />
            </Form.Item>
          </div>

          <Form.Item label="Program Types" style={styles.full}>
            <Checkbox.Group
              options={PROGRAM_TYPES}
              value={types}
              onChange={(vals) => setTypes(vals as string[])}
            />
          </Form.Item>

          <Form.Item label="Baseline Period" style={styles.full}>
            <Space>
              <DatePicker
                picker="month"
                value={baselineStart}
                onChange={setBaselineStart}
                placeholder="Start month"
              />
              <span style={{ color: 'rgba(0,0,0,0.45)' }}>to</span>
              <DatePicker
                picker="month"
                value={baselineEnd}
                onChange={setBaselineEnd}
                placeholder="End month"
              />
            </Space>
          </Form.Item>

          <Form.Item label="Notes" style={styles.full}>
            <Input.TextArea
              rows={2}
              value={notes}
              onChange={(evt) => setNotes(evt.target.value)}
              placeholder="Optional"
            />
          </Form.Item>

          <Divider plain>Baseline Conditions</Divider>

          <div style={styles.row}>
            <Form.Item label="Lease Hours" style={styles.half}>
              <Input
                value={conditions.leaseHours}
                onChange={(evt) =>
                  updateCondition('leaseHours', evt.target.value)
                }
                placeholder="e.g. Mon–Fri 8am–6pm"
              />
            </Form.Item>
            <Form.Item label="HVAC Schedule" style={styles.half}>
              <Input
                value={conditions.hvacSchedule}
                onChange={(evt) =>
                  updateCondition('hvacSchedule', evt.target.value)
                }
              />
            </Form.Item>
          </div>

          <div style={styles.row}>
            <Form.Item label="Lighting Schedule" style={styles.half}>
              <Input
                value={conditions.lightingSchedule}
                onChange={(evt) =>
                  updateCondition('lightingSchedule', evt.target.value)
                }
              />
            </Form.Item>
            <Form.Item label="Cleaning Schedule" style={styles.half}>
              <Input
                value={conditions.cleaningSchedule}
                onChange={(evt) =>
                  updateCondition('cleaningSchedule', evt.target.value)
                }
              />
            </Form.Item>
          </div>

          <Form.Item style={styles.full}>
            <Space direction="vertical">
              <Checkbox
                checked={conditions.staggeredStartupConfigured}
                onChange={(evt) =>
                  updateCondition(
                    'staggeredStartupConfigured',
                    evt.target.checked,
                  )
                }
              >
                Staggered Startup Configured
              </Checkbox>
              <Checkbox
                checked={conditions.demandLimitingConfigured}
                onChange={(evt) =>
                  updateCondition(
                    'demandLimitingConfigured',
                    evt.target.checked,
                  )
                }
              >
                Demand Limiting Configured
              </Checkbox>
              <Checkbox
                checked={conditions.optimalStartStopConfigured}
                onChange={(evt) =>
                  updateCondition(
                    'optimalStartStopConfigured',
                    evt.target.checked,
                  )
                }
              >
                Optimal Start/Stop Configured
              </Checkbox>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default ProgramEditButton;
